import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Activity, Scale, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const STATS = [
  { num: '500+', label: 'Facilities' },
  { num: '2M+', label: 'Animals tracked' },
  { num: '99.9%', label: 'Uptime SLA' },
];

function Sparkline() {
  // Static SVG sparkline used purely as decoration for the hero preview.
  const points = [4, 9, 6, 13, 10, 16, 12, 20, 17, 24, 22, 28];
  const w = 160;
  const h = 36;
  const max = Math.max(...points);
  const d = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - (p / max) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
      <defs>
        <linearGradient id="spark" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#A11212" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#A11212" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L${w},${h} L0,${h} Z`} fill="url(#spark)" />
      <path d={d} fill="none" stroke="#A11212" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HeroPreview({ alt }) {
  return (
    <div
      role="img"
      aria-label={alt}
      className="relative bg-white border border-ct-line ct-shadow-lg"
    >
      {/* Browser chrome */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-ct-line bg-[#FAF7F2]">
        <span className="w-2.5 h-2.5 rounded-full bg-[#E7E3DC]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#E7E3DC]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#E7E3DC]" />
        <span className="ml-3 text-[11px] font-mono text-ct-ink-3 truncate">
          app.carnitrack.com / operations
        </span>
      </div>

      {/* Body */}
      <div className="p-5 lg:p-6 space-y-4">
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-ct-ink-3">Live Operations</div>
            <div className="text-base font-black text-ct-ink" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Plant 04 · Morning Shift
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-ct-ink-2 bg-ct-live-soft text-[#166534] px-2 py-1">
            <span className="ct-live-dot" />
            <span className="uppercase tracking-wider text-[10px] font-bold">Online</span>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { k: 'Intake', v: '128', sub: 'today' },
            { k: 'Processed', v: '94', sub: '/hr' },
            { k: 'Avg. weight', v: '412', sub: 'kg' },
          ].map((m) => (
            <div key={m.k} className="border border-ct-line p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-ct-ink-3">{m.k}</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span
                  className="text-xl font-black text-ct-ink tabular-nums"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  {m.v}
                </span>
                <span className="text-[11px] text-ct-ink-3 font-mono">{m.sub}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Sparkline card */}
        <div className="border border-ct-line p-3">
          <div className="flex items-center justify-between mb-1">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-ct-ink-3">
              <Activity className="w-3 h-3" /> Throughput · last 12h
            </div>
            <div className="text-[11px] font-mono text-ct-brand">+18.4%</div>
          </div>
          <Sparkline />
        </div>

        {/* Line items */}
        <div className="border border-ct-line divide-y divide-ct-line">
          {[
            { icon: CheckCircle2, color: 'text-[#166534]', title: 'Lot #4721 passed QC', time: '09:41' },
            { icon: Scale, color: 'text-ct-ink-2', title: 'Station 03 · weight sync', time: '09:38' },
            { icon: AlertCircle, color: 'text-ct-brand', title: 'Intake queue at 92%', time: '09:31' },
          ].map((r, i) => {
            const I = r.icon;
            return (
              <div key={i} className="flex items-center gap-3 p-3">
                <I className={`w-4 h-4 flex-shrink-0 ${r.color}`} />
                <span className="flex-1 text-[13px] text-ct-ink font-medium">{r.title}</span>
                <span className="text-[11px] font-mono text-ct-ink-3">{r.time}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Floating badge */}
      <div className="hidden sm:flex absolute -bottom-4 -left-4 items-center gap-2 bg-ct-ink text-white px-3 py-2 ct-shadow-hard">
        <span className="ct-live-dot" />
        <span className="text-[11px] font-bold uppercase tracking-widest">12 facilities online</span>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const { pathFor, t } = useLanguage();

  return (
    <section className="relative bg-white border-b border-ct-line overflow-hidden">
      {/* Subtle grid backdrop */}
      <div aria-hidden="true" className="absolute inset-0 ct-grid-bg opacity-60" />
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-ct-brand/40 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
          {/* Left */}
          <div>
            <div className="ct-fade-up inline-flex items-center gap-2 border border-ct-brand/30 bg-ct-brand-soft/60 px-3 py-1 mb-6">
              <span className="ct-live-dot" />
              <span className="text-[11px] font-bold uppercase tracking-widest text-ct-brand-dark">
                {t.hero.badge}
              </span>
            </div>

            <h1
              className="ct-fade-up ct-fade-up-delay-1 text-4xl sm:text-5xl lg:text-[4rem] font-black text-ct-ink leading-[1.02] tracking-tight mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              {t.hero.headline1}{' '}
              <span className="relative inline-block text-ct-brand">
                {t.hero.headline2}
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 -bottom-1 h-[6px] bg-ct-brand/15"
                />
              </span>{' '}
              {t.hero.headline3}
            </h1>

            <p
              className="ct-fade-up ct-fade-up-delay-2 text-lg text-ct-ink-2 mb-8 leading-relaxed max-w-xl"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t.hero.subtext}
            </p>

            <div className="ct-fade-up ct-fade-up-delay-3 flex flex-wrap gap-3">
              <Link
                to={pathFor('/setup')}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-ct-brand hover:bg-ct-brand-dark text-white font-bold uppercase tracking-wider text-sm px-6 h-12 transition-colors duration-200 ct-shadow-md cursor-pointer"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to={pathFor('/signin')}
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white border border-ct-line-strong hover:border-ct-ink text-ct-ink font-bold uppercase tracking-wider text-sm px-6 h-12 transition-colors duration-200 cursor-pointer"
              >
                {t.hero.ctaSecondary}
              </Link>
            </div>

            <div className="ct-fade-up ct-fade-up-delay-4 mt-10 pt-8 border-t border-ct-line grid grid-cols-3 gap-6">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div
                    className="text-2xl sm:text-3xl font-black text-ct-ink tabular-nums"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    {s.num}
                  </div>
                  <div className="text-[11px] text-ct-ink-3 font-bold uppercase tracking-widest mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — product preview */}
          <div className="ct-fade-up ct-fade-up-delay-2 relative">
            <HeroPreview alt={t.hero.imageAlt} />
          </div>
        </div>
      </div>
    </section>
  );
}
